import { useEffect, useState, FormEvent } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Link, Stack, TextField, Typography } from '@mui/material';
import { authService } from '../../shared/services/auth.service';
import { useAuth } from '../../shared/hooks/useAuth';
import { useApiError } from '../../shared/errors/useApiError';
import { useNotification } from '../../shared/notifications/NotificationProvider';
import {
  SplitAuthLayout,
  MarketingBenefits,
} from '../../shared/components/layout/SplitAuthLayout';
import { validators } from '../../shared/validators/auth-validators';

const RESEND_COOLDOWN_SECONDS = 60;

export function VerifyEmailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, isLoading, patchUser } = useAuth();
  const { translate } = useApiError();
  const { notify } = useNotification();

  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldownLeft, setCooldownLeft] = useState(0);

  const nextParam = searchParams.get('next');
  const safeNext = nextParam && nextParam.startsWith('/') ? nextParam : '/dashboard';

  // Bounce out if the user lands here without a session, or if they already
  // finished this step.
  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    if (user?.emailVerified) {
      // Email already done — go straight to the destination. SMS phone
      // verification is disabled in the simplified signup (see AuthGuard).
      navigate(safeNext, { replace: true });
    }
  }, [isAuthenticated, isLoading, user, navigate, safeNext]);

  useEffect(() => {
    if (cooldownLeft <= 0) return;
    const id = setInterval(() => setCooldownLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldownLeft]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const err = validators.verificationCode(code.trim());
    if (err) {
      setCodeError(t(err));
      return;
    }
    setCodeError(null);
    setLoading(true);
    try {
      await authService.verifyEmail(code.trim());
      patchUser({ emailVerified: true });
      notify.success(t('auth.verify.emailSuccess'));
      // SMS phone verification is disabled — land straight on the destination.
      navigate(safeNext, { replace: true });
    } catch (e2) {
      notify.error(translate(e2));
    } finally {
      setLoading(false);
    }
  };

  const onResend = async () => {
    setResending(true);
    try {
      await authService.resendEmailVerification();
      setCooldownLeft(RESEND_COOLDOWN_SECONDS);
      notify.success(t('auth.verify.resentEmail'));
    } catch (e) {
      notify.error(translate(e));
    } finally {
      setResending(false);
    }
  };

  return (
    <SplitAuthLayout
      variant="customer"
      marketing={
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1.15, mb: 2 }}>
            {t('auth.verify.marketingTitle')}
          </Typography>
          <Typography variant="subtitle1" sx={{ color: 'inherit', opacity: 0.92 }}>
            {t('auth.verify.marketingSubtitle')}
          </Typography>
          <MarketingBenefits
            items={[
              t('auth.verify.marketingPoint1'),
              t('auth.verify.marketingPoint2'),
              t('auth.verify.marketingPoint3'),
            ]}
          />
        </Box>
      }
    >
      <Typography variant="h3" sx={{ mb: 1 }}>
        {t('auth.verify.emailTitle')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t('auth.verify.emailSubtitle', { email: user?.email ?? '' })}
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        {t('auth.verify.stepIndicator', { step: 1, total: 2 })}
      </Alert>

      <Box component="form" onSubmit={onSubmit} noValidate>
        <Stack spacing={2}>
          <TextField
            label={t('auth.verify.codeLabel')}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            required
            autoFocus
            fullWidth
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            error={!!codeError}
            helperText={codeError || t('auth.verify.codeHint')}
            inputProps={{ maxLength: 6, pattern: '\\d{6}' }}
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loading}
            fullWidth
            sx={{ py: 1.5 }}
          >
            {loading ? t('common.loading') : t('auth.verify.submit')}
          </Button>

          <Button
            onClick={onResend}
            disabled={resending || cooldownLeft > 0}
            variant="text"
          >
            {cooldownLeft > 0
              ? t('auth.verify.resendInSeconds', { seconds: cooldownLeft })
              : t('auth.verify.resend')}
          </Button>
        </Stack>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
        <Link component={RouterLink} to="/login" sx={{ color: 'primary.main', fontWeight: 600 }}>
          {t('auth.backToLogin')}
        </Link>
      </Typography>
    </SplitAuthLayout>
  );
}
